/**
 * CR164: Deal Locator Types
 * Types for Deep Keyword Scan, tiled scanning, and locator region detection
 */

// ============================================================================
// DEAL LOCATOR CONFIG TYPES
// ============================================================================

/**
 * Deep Keyword Scan configuration for deal region detection
 */
export interface DealLocatorConfig {
  /** Master toggle for deep keyword scan */
  enabled: boolean;
  
  /** Run mode settings */
  runOnCandidatesOnly: boolean;
  runOnAllPages: boolean;
  
  /** Output settings */
  createLocatorRegions: boolean;
  maxRegionsPerPage: number;
  
  /** Keywords for detection (one per entry) */
  keywords: string[];
  
  /** Tight matching controls */
  tightMatching: TightMatchingConfig;
}

export interface TightMatchingConfig {
  /** Require gift-card context for non-GC keywords */
  requireGiftCardContext: boolean;
  /** Context window in characters for context checks */
  contextWindowChars: number;
  /** Require money cue for money phrases */
  requireMoneyCue: boolean;
}

/**
 * Large Page Tiled Scanning configuration
 */
export interface TiledScanningConfig {
  /** Enable tiled scanning on large pages */
  enabled: boolean;
  /** Large page threshold: longest edge in pixels */
  largePageEdgeThreshold: number;
  /** Large page threshold: area in pixels */
  largePageAreaThreshold: number;
  /** Tile size in pixels */
  tileSizePx: number;
  /** Tile overlap in pixels */
  tileOverlapPx: number;
}

/**
 * Extraction Focus configuration
 */
export interface ExtractionFocusConfig {
  /** Prefer locator crops for AI extraction */
  preferLocatorCrops: boolean;
  /** Crop padding in pixels */
  cropPaddingPx: number;
  /** Maximum crops per page for AI extraction */
  maxCropsPerPage: number;
  /** Fall back to full-page AI if no locator regions found */
  fallbackToFullPage: boolean;
}

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_DEAL_LOCATOR_KEYWORDS = [
  "gift card",
  "giftcard",
  "egift",
  "bonus",
  "points",
  "when you spend",
  "get a $",
  "spend $",
];

export const DEFAULT_DEAL_LOCATOR_CONFIG: DealLocatorConfig = {
  enabled: true,
  runOnCandidatesOnly: true,
  runOnAllPages: false,
  createLocatorRegions: true,
  maxRegionsPerPage: 5,
  keywords: DEFAULT_DEAL_LOCATOR_KEYWORDS,
  tightMatching: {
    requireGiftCardContext: true,
    contextWindowChars: 80,
    requireMoneyCue: true,
  },
};

export const DEFAULT_TILED_SCANNING_CONFIG: TiledScanningConfig = {
  enabled: true,
  largePageEdgeThreshold: 5000,
  largePageAreaThreshold: 25_000_000, // ~25 megapixels
  tileSizePx: 1536,
  tileOverlapPx: 192,
};

export const DEFAULT_EXTRACTION_FOCUS_CONFIG: ExtractionFocusConfig = {
  preferLocatorCrops: true,
  cropPaddingPx: 80,
  maxCropsPerPage: 3,
  fallbackToFullPage: true,
};

// ============================================================================
// LOCATOR RESULT TYPES
// ============================================================================

export type TightMatchRule = 
  | "GIFT_INDICATOR_DIRECT"  // keyword is gift card/giftcard/egift
  | "CONTEXT_WINDOW_MATCH"   // found gift indicator within context window
  | "MONEY_CUE_FOUND"        // required money cue was found
  | "NO_RULE_REQUIRED";      // no tight matching rule needed

/**
 * A single locator region detected on a page
 */
export interface LocatorRegion {
  /** Unique ID for this region */
  id: string;
  /** Matched keyword(s) */
  matchedKeywords: string[];
  /** Text snippet around the match (±chars from context window) */
  snippet: string;
  /** Bounding box coordinates */
  coords: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** Which tight matching rule was satisfied */
  ruleSatisfied: TightMatchRule;
  /** Additional details about rule satisfaction */
  ruleDetails?: string;
  /** Confidence/score for this region */
  score: number;
  /** Tile index if from tiled scanning */
  tileIndex?: number;
}

/**
 * Tile OCR result for tiled scanning
 */
export interface TileOcrResult {
  /** Tile index (0-based) */
  index: number;
  /** Tile coordinates on the page */
  coords: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** OCR text from this tile */
  text: string;
  /** OCR confidence */
  confidence: number;
  /** Keywords matched in this tile */
  matchedKeywords: string[];
  /** Locator regions found in this tile */
  locatorRegions: LocatorRegion[];
}

/**
 * Complete locator results for a page
 */
export interface PageLocatorResults {
  /** Page number */
  pageNumber: number;
  /** Whether locator was run on this page */
  locatorRan: boolean;
  /** Reason if locator was skipped */
  skipReason?: string;
  /** Whether tiled scanning was used */
  tiledScanningUsed: boolean;
  /** Number of tiles (if tiled) */
  tileCount?: number;
  /** Individual tile results (if debug enabled) */
  tileResults?: TileOcrResult[];
  /** Locator regions found */
  regions: LocatorRegion[];
  /** Total locator duration in ms */
  durationMs: number;
  /** Whether AI extraction used crops from locator */
  aiUsedCrops: boolean;
  /** Crop artifact paths used for AI */
  cropArtifactPaths?: string[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Normalize a keyword: lowercase, trim, collapse spaces
 */
export function normalizeKeyword(keyword: string): string {
  return keyword.toLowerCase().trim().replace(/\s+/g, " ");
}

/**
 * Parse keywords from multiline text input
 */
export function parseKeywordsFromText(text: string): string[] {
  return text
    .split("\n")
    .map(line => normalizeKeyword(line))
    .filter(line => line.length > 0);
}

/**
 * Check if a keyword is a direct gift-card indicator
 */
export function isGiftCardIndicator(keyword: string): boolean {
  const normalized = normalizeKeyword(keyword);
  return ["gift card", "giftcard", "egift"].includes(normalized);
}

/**
 * Check if a keyword is a money phrase requiring money cue validation
 */
export function isMoneyPhrase(keyword: string): boolean {
  const normalized = normalizeKeyword(keyword);
  return ["get a $", "spend $", "when you spend"].some(mp => normalized.includes(mp));
}

/**
 * Check if text contains a money cue (currency pattern)
 */
export function hasMoneyCue(text: string): boolean {
  // Match $ followed by digits, or "more" patterns
  return /\$\s*\d+|or more/i.test(text);
}

/**
 * Check if text contains a gift indicator within a window
 */
export function hasGiftIndicatorInWindow(
  text: string, 
  matchPosition: number, 
  windowChars: number
): boolean {
  const start = Math.max(0, matchPosition - windowChars);
  const end = Math.min(text.length, matchPosition + windowChars);
  const windowText = text.substring(start, end).toLowerCase();
  return /gift\s*card|giftcard|egift/i.test(windowText);
}

/**
 * Determine which tight matching rule is satisfied for a match
 */
export function determineTightMatchRule(
  keyword: string,
  fullText: string,
  matchPosition: number,
  config: TightMatchingConfig
): { passed: boolean; rule: TightMatchRule; details?: string } {
  const normalizedKeyword = normalizeKeyword(keyword);
  
  // Rule 1: Gift-card context - direct indicators always pass
  if (isGiftCardIndicator(normalizedKeyword)) {
    return { 
      passed: true, 
      rule: "GIFT_INDICATOR_DIRECT",
      details: "Keyword is a direct gift-card indicator"
    };
  }
  
  // For non-GC keywords, check context if required
  if (config.requireGiftCardContext) {
    if (!hasGiftIndicatorInWindow(fullText, matchPosition, config.contextWindowChars)) {
      return { 
        passed: false, 
        rule: "CONTEXT_WINDOW_MATCH",
        details: `No gift indicator found within ${config.contextWindowChars} chars`
      };
    }
  }
  
  // Rule 2: Money cue requirement for money phrases
  if (config.requireMoneyCue && isMoneyPhrase(normalizedKeyword)) {
    if (!hasMoneyCue(fullText)) {
      return { 
        passed: false, 
        rule: "MONEY_CUE_FOUND",
        details: "Money phrase found but no currency pattern in text"
      };
    }
    return { 
      passed: true, 
      rule: "MONEY_CUE_FOUND",
      details: "Money cue validation passed"
    };
  }
  
  // Rule 3: bonus/points require gift indicator
  if (["bonus", "points"].includes(normalizedKeyword)) {
    if (config.requireGiftCardContext) {
      if (!hasGiftIndicatorInWindow(fullText, matchPosition, config.contextWindowChars)) {
        return { 
          passed: false, 
          rule: "CONTEXT_WINDOW_MATCH",
          details: `"${normalizedKeyword}" requires gift indicator in context window`
        };
      }
    }
    return { 
      passed: true, 
      rule: "CONTEXT_WINDOW_MATCH",
      details: "Gift indicator found in context window"
    };
  }
  
  // Default: context window check passed or not required
  return { 
    passed: true, 
    rule: config.requireGiftCardContext ? "CONTEXT_WINDOW_MATCH" : "NO_RULE_REQUIRED",
    details: config.requireGiftCardContext ? "Context window validation passed" : "No rule required"
  };
}
